import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Button, Card } from '@helix-ds/react'
import { Messages, UsersGroup } from 'flowbite-react-icons/outline'
import { AlertBanner } from '@/components/common/AlertBanner'
import { Navbar } from '@/components/Navbar'
import { getClientName } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useLaunchMode } from '@/features/auth/launchContext'
import { PermissionGuard } from '@/features/authorization/PermissionGuard'
import { listMyCases } from '@/features/caseConnect/services/caseConnectClient'
import type { CaseStatus, CaseSummaryRow } from '@/features/caseConnect/types'

const HEADING = 'text-text-heading dark:text-white font-semibold tracking-tight'
const SUBTLE = 'text-sm font-medium text-text-body-subtle dark:text-gray-500'

// Tabs filter the (already-scoped) list client-side. The case status domain is
// only OPEN/CLOSED today (there is no IN_REVIEW status in the schema), so we map
// Active=OPEN and Closed=CLOSED and OMIT an "In Review" tab — see the cell19
// deviation note. Add an IN_REVIEW tab here if/when the status is introduced.
type CasesTab = 'ALL' | 'OPEN' | 'CLOSED'

const TABS: { id: CasesTab; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'OPEN', label: 'Active' },
  { id: 'CLOSED', label: 'Closed' },
]

function useCaller(): string {
  const { user } = useAuth0()
  return user?.email || user?.sub || 'unknown-user'
}

/**
 * Standalone page shell (Navbar + padded container) mirroring CaseConnect's
 * shell so the landing page has the app header in every state. In Epic
 * (`epic_smart`) mode the Navbar renders null, matching the case page.
 */
function CasesListShell({ children }: { children: ReactNode }) {
  const { config } = useUiConfig()
  const { mode: launchMode } = useLaunchMode()
  const { user: auth0User, logout: auth0Logout } = useAuth0()
  const authFlow = launchMode === 'epic_smart' ? 'epic' : 'standalone'

  const handleLogout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
  }

  // The Navbar itself suppresses the org chip for a case-only (org-less)
  // participant (see Navbar `isCaseOnly`), so we can pass the client-config org
  // name unconditionally here: an org member sees their org, an org-less
  // participant sees none. Each case's own org is shown on its card.
  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
        />
      )}
      <div className={`flex w-full flex-col gap-4 ${authFlow === 'standalone' ? 'px-6' : ''}`}>{children}</div>
    </div>
  )
}

/** Status badge (OPEN -> Active brand pill, CLOSED -> muted). */
function StatusBadge({ status }: { status: CaseStatus }) {
  const isClosed = status === 'CLOSED'
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold ${
        isClosed
          ? 'bg-background-tertiary dark:bg-gray-800 text-text-body dark:text-gray-400'
          : 'bg-brand-50 dark:bg-gray-800 text-brand-700 dark:text-brand-400'
      }`}
    >
      {isClosed ? 'Closed' : 'Active'}
    </span>
  )
}

/** One case card, linking to /cases/:caseId. */
function CaseCard({ row }: { row: CaseSummaryRow }) {
  return (
    <Link
      to={`/cases/${row.caseId}`}
      className="block rounded-base focus:outline-none focus:ring focus:ring-brand-600"
      aria-label={`Open case ${row.title || row.displayCode || row.caseId}`}
    >
      <Card className="transition-shadow hover:shadow-card-hover">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {row.displayCode && (
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-400">
                {row.displayCode}
              </p>
            )}
            <h2 className={`${HEADING} mt-0.5 truncate text-base`}>{row.title || 'Case Conference'}</h2>
            {row.orgName && (
              <p className="mt-0.5 truncate text-xs font-medium text-text-body-subtle dark:text-gray-500">{row.orgName}</p>
            )}
          </div>
          <StatusBadge status={row.status} />
        </div>
        {row.summary && (
          <p className="mt-2 line-clamp-2 text-sm font-medium text-text-body dark:text-gray-400">{row.summary}</p>
        )}
        <div className="mt-3 flex items-center gap-4 text-xs font-medium text-text-body-subtle dark:text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <UsersGroup className="h-4 w-4" aria-hidden />
            {row.participantCount} {row.participantCount === 1 ? 'participant' : 'participants'}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Messages className="h-4 w-4" aria-hidden />
            {row.messageCount} {row.messageCount === 1 ? 'message' : 'messages'}
          </span>
        </div>
      </Card>
    </Link>
  )
}

/** The Clinician Connect landing page: the caller's cases with tab filters. */
export function CasesList() {
  const caller = useCaller()
  const [cases, setCases] = useState<CaseSummaryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<CasesTab>('ALL')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await listMyCases(caller)
      setCases(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load your cases')
    } finally {
      setLoading(false)
    }
  }, [caller])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    if (tab === 'ALL') return cases
    return cases.filter((c) => c.status === tab)
  }, [cases, tab])

  return (
    <CasesListShell>
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 py-6">
        <header className="flex flex-col gap-1">
          <h1 className={`${HEADING} text-2xl`}>Case Connect</h1>
          <p className={SUBTLE}>Cases you can collaborate on.</p>
        </header>

        <div role="tablist" aria-label="Filter cases by status" className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = tab === t.id
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium focus:ring focus:ring-brand-600 ${
                  active
                    ? 'border-brand-600 dark:border-brand-500 bg-brand-600 text-white'
                    : 'border-border-base dark:border-gray-700 text-text-body dark:text-gray-400 hover:bg-brand-600 hover:text-white'
                }`}
              >
                {t.label}
              </button>
            )
          })}
        </div>

        {loading ? (
          <p className={SUBTLE}>Loading your cases…</p>
        ) : error ? (
          <AlertBanner variant="error">
            <span>{error}</span>{' '}
            <button type="button" onClick={() => void load()} className="font-medium underline hover:opacity-80">
              Retry
            </button>
          </AlertBanner>
        ) : filtered.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <h2 className={`${HEADING} text-lg`}>
                {cases.length === 0 ? 'You have no cases yet' : 'No cases match this filter'}
              </h2>
              <p className={SUBTLE}>
                {cases.length === 0
                  ? 'Cases you are invited to will appear here.'
                  : 'Try a different tab to see your other cases.'}
              </p>
              {cases.length > 0 && tab !== 'ALL' && (
                <Button type="button" variant="secondary" onClick={() => setTab('ALL')}>
                  Show all cases
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <ul className="flex flex-col gap-4">
            {filtered.map((row) => (
              <li key={row.caseId}>
                <CaseCard row={row} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </CasesListShell>
  )
}

/** Route-level guard: only case:view holders may see the landing page. */
export function CasesListRoute() {
  return (
    <PermissionGuard
      permission="case:view"
      showFallback
      fallback={
        <div className="p-6 text-sm font-medium text-text-body-subtle dark:text-gray-500" role="alert">
          You do not have access to Case Connect.
        </div>
      }
    >
      <CasesList />
    </PermissionGuard>
  )
}
